/**
 * Category select dropdown with search
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useCategories } from '@/hooks';
import type { Category, CategoryType } from '@/types';
import { cn } from '@/utils/cn';

export interface CategorySelectProps {
  value: string | undefined;
  onChange: (categoryId: string | undefined) => void;
  placeholder?: string;
  allowClear?: boolean;
  className?: string;
  disabled?: boolean;
  /**
   * Filter categories by type.
   * - 'income': Show only income categories (for positive amount transactions)
   * - 'expense': Show only expense categories (for negative amount transactions)
   * - 'transfer': Show only transfer categories
   * - undefined: Show all categories (default, used for bulk operations)
   */
  categoryType?: CategoryType;
}

/**
 * Searchable category dropdown
 */
export default function CategorySelect({
  value,
  onChange,
  placeholder = 'Select category',
  allowClear = true,
  className,
  disabled = false,
  categoryType,
}: CategorySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch categories filtered by type if specified
  const { data: categories = [], isLoading } = useCategories({ type: categoryType });

  // Find selected category
  const selectedCategory = useMemo(() => {
    if (!value) return null;
    return categories.find((cat) => cat.id === value) || null;
  }, [value, categories]);

  // Filter categories by search query and include ancestors for context
  const { filteredCategories, matchingIds } = useMemo(() => {
    if (!searchQuery) {
      return { filteredCategories: categories, matchingIds: new Set<string>() };
    }

    const query = searchQuery.toLowerCase();

    // First pass: find all categories that directly match the search
    const directMatches = new Set<string>();
    categories.forEach((cat) => {
      if (
        cat.name?.toLowerCase()?.includes(query) ||
        cat.path?.toLowerCase()?.includes(query)
      ) {
        directMatches.add(cat.id);
      }
    });

    // Second pass: collect all ancestor IDs that need to be included
    const ancestorIds = new Set<string>();
    const categoryById = new Map(categories.map((c) => [c.id, c]));

    directMatches.forEach((matchId) => {
      let current = categoryById.get(matchId);
      while (current?.parentId) {
        ancestorIds.add(current.parentId);
        current = categoryById.get(current.parentId);
      }
    });

    // Combine direct matches with their ancestors
    const includedIds = new Set([...directMatches, ...ancestorIds]);
    const filtered = categories.filter((cat) => includedIds.has(cat.id));

    return { filteredCategories: filtered, matchingIds: directMatches };
  }, [categories, searchQuery]);

  // Group categories by parent for hierarchical display
  const groupedCategories = useMemo(() => {
    const roots: Category[] = [];
    const childrenMap = new Map<string, Category[]>();

    filteredCategories.forEach((cat) => {
      if (!cat.parentId) {
        roots.push(cat);
      } else {
        const children = childrenMap.get(cat.parentId) || [];
        children.push(cat);
        childrenMap.set(cat.parentId, children);
      }
    });

    return { roots, childrenMap };
  }, [filteredCategories]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (category: Category) => {
    onChange(category.id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const renderCategory = (category: Category, level: number = 0) => {
    const children = groupedCategories.childrenMap.get(category.id) || [];
    const isSelected = value === category.id;
    // When searching, distinguish between direct matches and ancestor context
    const isDirectMatch = searchQuery ? matchingIds.has(category.id) : true;
    const isAncestorContext = Boolean(searchQuery) && !isDirectMatch;

    return (
      <div key={category.id}>
        <button
          type="button"
          className={cn(
            'w-full px-3 py-2 text-left text-sm focus:outline-none',
            isSelected && 'bg-primary-50 text-primary-700',
            !isSelected && isDirectMatch && 'hover:bg-gray-100 focus:bg-gray-100',
            // Ancestors shown for context: dimmed and non-interactive for selection
            isAncestorContext && 'text-gray-400 cursor-default'
          )}
          style={{ paddingLeft: `${12 + level * 16}px` }}
          onClick={() => isDirectMatch && handleSelect(category)}
          // Ancestors are not selectable - they're just for context
          disabled={isAncestorContext}
        >
          <span className={cn(isDirectMatch && searchQuery && 'font-medium')}>
            {category.name}
          </span>
        </button>
        {children.map((child) => renderCategory(child, level + 1))}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-left bg-white border rounded-lg shadow-sm',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'border-gray-300',
          isOpen && 'ring-2 ring-primary-500 border-primary-500'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={cn('block truncate', !selectedCategory && 'text-gray-400')}>
          {selectedCategory ? selectedCategory.name : placeholder}
        </span>
        <span className="flex items-center">
          {allowClear && selectedCategory && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="mr-1 text-gray-400 hover:text-gray-600"
              aria-label="Clear selection"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
          <svg
            className={cn('h-5 w-5 text-gray-400 transition-transform', isOpen && 'transform rotate-180')}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Search categories..."
              aria-label="Search categories"
            />
          </div>

          {/* Category list */}
          <div className="max-h-48 overflow-y-auto" role="listbox">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
            ) : filteredCategories.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No categories found</div>
            ) : (
              groupedCategories.roots.map((category) => renderCategory(category))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
